import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { authApi, TOTPSetupResponse } from '@/lib/api/auth'
import { User } from '@/types/user'
import toast from 'react-hot-toast'

interface PendingTwoFactor {
  tempToken: string
  type: '2fa' | '2fa_setup'
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  pendingTwoFactor: PendingTwoFactor | null
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  logout: (logoutFromProvider?: boolean) => Promise<void>
  refreshToken: () => Promise<string>
  verify2FA: (code: string) => Promise<void>
  setup2FA: () => Promise<TOTPSetupResponse>
  confirm2FA: (code: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [pendingTwoFactor, setPendingTwoFactor] = useState<PendingTwoFactor | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Check for existing session on mount - always check to handle redirects properly
  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const userData = await authApi.getCurrentUser()
      setUser(userData)
    } catch (error) {
      // Cookies will be cleared by the backend if needed
      console.error('Auth check failed:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string, rememberMe = false) => {
    try {
      const response = await authApi.login({ email, password, remember_me: rememberMe })

      // Check if 2FA is required
      if (response.requires_2fa && response.temp_token) {
        setPendingTwoFactor({ tempToken: response.temp_token, type: '2fa' })
        navigate('/login/2fa', { replace: true })
        return
      }

      // Check if 2FA setup is required (admin accounts)
      if (response.requires_2fa_setup && response.temp_token) {
        setPendingTwoFactor({ tempToken: response.temp_token, type: '2fa_setup' })
        navigate('/login/2fa-setup', { replace: true })
        return
      }

      // Normal login — cookies set by backend
      setUser(response.user)
      toast.success('Welcome back!')

      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Login failed'
      toast.error(message)
      throw error
    }
  }

  const verify2FA = async (code: string) => {
    if (!pendingTwoFactor) throw new Error('No pending 2FA verification')

    try {
      const response = await authApi.verify2FA(pendingTwoFactor.tempToken, code)
      setPendingTwoFactor(null)
      setUser(response.user)
      toast.success('Welcome back!')
      navigate('/dashboard', { replace: true })
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Invalid code'
      toast.error(message)
      throw error
    }
  }

  const setup2FA = async (): Promise<TOTPSetupResponse> => {
    if (!pendingTwoFactor) throw new Error('No pending 2FA setup')
    return authApi.setup2FA(pendingTwoFactor.tempToken)
  }

  const confirm2FA = async (code: string) => {
    if (!pendingTwoFactor) throw new Error('No pending 2FA setup')

    try {
      const response = await authApi.confirm2FA(code, pendingTwoFactor.tempToken)
      setPendingTwoFactor(null)

      if (response.user) {
        setUser(response.user)
        toast.success('2FA enabled! Welcome back!')
        navigate('/dashboard', { replace: true })
      } else {
        toast.success('Two-factor authentication enabled!')
      }
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Invalid code'
      toast.error(message)
      throw error
    }
  }

  const logout = async (logoutFromProvider = false) => {
    try {
      // If user is an SSO user and wants to logout from provider
      if (user?.is_oauth_user && logoutFromProvider && user.oauth_provider === 'microsoft') {
        const tenantId = 'common'
        const logoutUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin + '/login')}`

        await authApi.logout()
        setUser(null)

        window.location.href = logoutUrl
        return
      }

      await authApi.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      setPendingTwoFactor(null)
      navigate('/login')
      toast.success('Logged out successfully')
    }
  }

  const refreshToken = async () => {
    try {
      const response = await authApi.refreshToken()
      return response.access_token
    } catch (error) {
      throw error
    }
  }

  return (
    <AuthContext.Provider value={{
      user, isLoading, pendingTwoFactor,
      login, logout, refreshToken,
      verify2FA, setup2FA, confirm2FA,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
